'use client';

import type { ReactNode } from 'react';
// eslint-disable-next-line react/no-use-context -- useContext is more stable for SSR/hydration than use()
import { createContext, useContext, useState } from 'react';
import { useStore } from 'zustand';

import type { EditingLanguageStoreApi } from './store';
import { createEditingLanguageStore } from './store';
import type { EditingLanguageStore } from './types';

export type { EditingLanguageStoreApi } from './store';

export const EditingLanguageStoreContext = createContext<EditingLanguageStoreApi | undefined>(
  undefined,
);

type EditingLanguageStoreProviderProps = {
  children: ReactNode;
};

export function EditingLanguageStoreProvider({
  children,
}: EditingLanguageStoreProviderProps) {
  const [store] = useState<EditingLanguageStoreApi>(() =>
    createEditingLanguageStore(),
  );

  return (
    <EditingLanguageStoreContext value={store}>
      {children}
    </EditingLanguageStoreContext>
  );
}

// eslint-disable-next-line react-refresh/only-export-components
export function useEditingLanguageStore<T>(
  selector: (store: EditingLanguageStore) => T,
): T {
  // eslint-disable-next-line react/no-use-context -- useContext is more stable for SSR/hydration
  const editingLanguageStoreContext = useContext(EditingLanguageStoreContext);

  if (editingLanguageStoreContext === undefined) {
    throw new Error('useEditingLanguageStore must be used within EditingLanguageStoreProvider');
  }

  return useStore(editingLanguageStoreContext, selector);
}

// eslint-disable-next-line react-refresh/only-export-components
export function useEditingLanguageStoreApi(): EditingLanguageStoreApi {
  // eslint-disable-next-line react/no-use-context -- useContext is more stable for SSR/hydration
  const editingLanguageStoreContext = useContext(EditingLanguageStoreContext);

  if (editingLanguageStoreContext === undefined) {
    throw new Error('useEditingLanguageStoreApi must be used within EditingLanguageStoreProvider');
  }

  return editingLanguageStoreContext;
}
